/**
 * Reducer. This should be consumed by useImmerReducer()
 *
 */

import { ExamBlobs, Question } from "@examspark/types";

enum ActionType {
  CLEAR = "CLEAR",
  INIT = "INIT",
  REVEAL_KEY = "REVEAL_KEY",
  SCORE_RESPONSE = "SCORE_RESPONSE",
  UNSCORE_RESPONSE = "UNSCORE_RESPONSE",
  RESET = "RESET",
  RESPONSE = "RESPONSE",
  HAS_MEDIA = "HAS_MEDIA",
  SHUFFLE = "SHUFFLE",
}

// todo: move all hardcoded strings to ActionType enum
export type Action =
  | {
      type: ActionType.INIT | "INIT";
      data: Partial<QuestionState>;
    }
  | {
      type: ActionType.REVEAL_KEY | "REVEAL_KEY";
      data?: {
        // send a response along with the key, for the question
        // types who need it (extended text, etc)
        response: any;
      };
    }
  | {
      type: ActionType.SCORE_RESPONSE | "SCORE_RESPONSE";
    }
  | {
      type: ActionType.UNSCORE_RESPONSE | "UNSCORE_RESPONSE";
    }
  | {
      type: ActionType.RESET | "RESET";
    }
  | {
      type: ActionType.RESPONSE | "RESPONSE";
      data:
        | {
            id: string;
            response: any;
            score?: boolean;
            // blobs for a single interaction
            blobs?: ExamBlobs[string][string];
          }
        | {
            // submit the entire responses object
            id?: never;
            response: any;
            blobs?: ExamBlobs[string];
            score?: boolean;
            viewOnly?: boolean;
          };
    }
  | {
      type: ActionType.HAS_MEDIA | "HAS_MEDIA";
    }
  | {
      type: ActionType.CLEAR | "CLEAR";
    }
  | {
      // undefined seed means unshuffle
      type: ActionType.SHUFFLE | "SHUFFLE";
      seed: string | undefined;
    };

export type QuestionState = {
  response: Record<string, any> | undefined;
  question?: Question;
  revealKey?: boolean;
  scoreResponses?: boolean;
  shuffleSeed?: string;

  // question types that store their data as a blob
  // to s3 can do so here and the autosave mechanism
  // will pick it up and do the needful
  blobs?: ExamBlobs[string];

  // currently, this is set in the item previewer.
  // I can see us also using i t in the player
  // for an 'enhanced' pause mode
  viewOnly?: boolean;

  // not used (yet?)
  hasMedia?: boolean;
};

export const reducer = (draft: QuestionState, action: Action) => {
  switch (action.type) {
    case ActionType.CLEAR:
      draft = {
        response: {},
      };
      return draft;
    case ActionType.INIT:
      draft = Object.assign({ response: {} }, action.data);
      if (draft.response == null) {
        draft.response = {};
      }
      return draft;
    case ActionType.REVEAL_KEY:
      draft.revealKey = true;
      if (action.data?.response) {
        draft.response = action.data.response;
      }
      return draft;
    case ActionType.SCORE_RESPONSE:
      draft.scoreResponses = true;
      return draft;
    case ActionType.UNSCORE_RESPONSE:
      draft.scoreResponses = false;
      return draft;
    case ActionType.RESET:
      draft.response = {};
      draft.scoreResponses = false;
      draft.revealKey = false;
      return draft;
    case ActionType.SHUFFLE:
      draft.shuffleSeed = action.seed;
      return draft;
    case ActionType.RESPONSE:
      if (draft.scoreResponses) {
        draft.scoreResponses = false;
      }
      if (action.data.score) {
        draft.scoreResponses = true;
      }
      draft.revealKey = false;
      if (action.data.id != null) {
        if (!draft.response) draft.response = {};
        draft.response[action.data.id] = action.data.response;

        if (action.data.blobs) {
          if (!draft.blobs) draft.blobs = {};
          Object.entries(action.data.blobs).forEach(([key, value]) => {
            if (!draft.blobs![action.data.id!]) {
              draft.blobs![action.data.id!] = {};
            }
            draft.blobs![action.data.id!][key] = value;
          });
        }
      } else {
        if (action.data.viewOnly != null) {
          draft.viewOnly = action.data.viewOnly;
        }
        draft.response = action.data.response;
        draft.blobs = action.data.blobs;
      }

      return draft;
    case ActionType.HAS_MEDIA:
      // draft.hasMedia = true;
      return draft;

    default:
      console.error("Unknown action type in QuestionContext reducer", action);
      return draft;
  }
};
